import { SIDE } from 'constants/enums';
import { decimalMultiplication } from 'helpers/assetUtils';
import { divide } from 'helpers/ramda';
import { calcPrecision, cropAfterDecimals, isNan } from 'helpers/utils';
import { selectedProductMarkPriceSelector } from 'stores/selectedProduct/selectedProduct.selectors';
import { NotionalType, Product } from 'types/IProducts';

import { notional, sanitizeMaxQuantity } from './commonFormulae';

const getMarkPrice = (tickSize: number) => {
  const precision = calcPrecision(tickSize);
  const markPrice = selectedProductMarkPriceSelector(precision);
  return markPrice;
};

/**
 * Converts input value from quantity input to number of contracts for inverse contracts
 * @param inputedQuantity user inputted value
 * @param selectedProduct
 * @param selectedCurrency selected currency from quantity dropdown
 * @returns number of contracts
 */
const quantityToContractsForInverse = ({
  inputedQuantity,
  selectedProduct,
  selectedCurrency,
}: {
  inputedQuantity: number;
  selectedProduct: Product;
  selectedCurrency: string;
}): number => {
  try {
    const quotingAsset = selectedProduct?.quoting_asset;
    const underlyingAsset = selectedProduct?.underlying_asset;
    const contractValue = Number(selectedProduct?.contract_value ?? '1');

    const quotingAssetSymbol = quotingAsset?.symbol;
    const underlyingAssetSymbol = underlyingAsset?.symbol;
    const markPrice = isNan(getMarkPrice(Number(selectedProduct?.tick_size)))
      ? 0
      : Number(getMarkPrice(Number(selectedProduct?.tick_size)));

    if (selectedCurrency === quotingAssetSymbol) {
      return parseInt(String(divide(inputedQuantity, contractValue)), 10);
    }
    if (selectedCurrency === underlyingAssetSymbol) {
      return parseInt(
        String(
          divide(Number(decimalMultiplication(inputedQuantity, markPrice)), contractValue)
        ),
        10
      );
    }

    return parseInt(String(inputedQuantity), 10);
  } catch (error) {
    console.error('Error in quantityToContractsForInverse:', error);
    return 0;
  }
};

/**
 * Converts number of contracts to the amount of selected asset for inverse contracts
 * @param orderSize In number of contracts
 * @param selectedProduct
 * @param selectedCurrency
 * @returns
 */
const contractToAssetForInverse = ({
  orderSize,
  selectedProduct,
  selectedCurrency,
}: {
  orderSize: number;
  selectedProduct: Product;
  selectedCurrency: string;
}) => {
  try {
    const quotingAsset = selectedProduct?.quoting_asset;
    const underlyingAsset = selectedProduct?.underlying_asset;
    const contractValue = Number(selectedProduct?.contract_value ?? '1');

    if (!quotingAsset || !underlyingAsset) {
      throw new Error('Quoting asset and underlying asset are required');
    }

    const quotingAssetSymbol = quotingAsset?.symbol;
    const underlyingAssetSymbol = underlyingAsset?.symbol;
    const markPrice = isNan(getMarkPrice(Number(selectedProduct?.tick_size)))
      ? 0
      : Number(getMarkPrice(Number(selectedProduct?.tick_size)));

    if (!quotingAssetSymbol || !underlyingAssetSymbol) {
      throw new Error('Quoting asset symbol and underlying asset symbol are required');
    }

    const formula = orderSize * contractValue;
    if (selectedCurrency === quotingAssetSymbol) {
      return cropAfterDecimals(formula, quotingAsset?.minimum_precision ?? 2);
    }
    if (isNan(markPrice)) {
      throw new Error('Mark price is not available');
    }
    if (selectedCurrency === underlyingAssetSymbol) {
      const precision =
        underlyingAssetSymbol === 'ETH'
          ? underlyingAsset?.minimum_precision
          : underlyingAsset?.precision;
      return cropAfterDecimals(divide(formula, markPrice), precision ?? 2);
    }

    return 0;
  } catch (error) {
    console.error('Error in contractToAssetForInverse:', error);
    return 0;
  }
};

/**
 * Converts input value from quantity input to number of contracts for futures contract type
 * @param inputedQuantity user inputted value
 * @param selectedProduct
 * @param selectedCurrency selected currency from quantity dropdown
 * @returns number of contracts
 */
const quantityToContractsForFutures = ({
  inputedQuantity,
  selectedProduct,
  selectedCurrency,
}: {
  inputedQuantity: number;
  selectedProduct: Product;
  selectedCurrency: string;
}): number => {
  try {
    if (!selectedProduct) {
      throw new Error('Selected product is required');
    }
    if (!selectedCurrency) {
      throw new Error('Selected currency is required');
    }

    const quotingAsset = selectedProduct?.quoting_asset;
    const underlyingAsset = selectedProduct?.underlying_asset;
    const notionalType = selectedProduct?.notional_type;
    const contractValue = Number(selectedProduct?.contract_value ?? '1');

    if (!quotingAsset || !underlyingAsset) {
      throw new Error('Quoting asset and underlying asset are required');
    }

    const quotingAssetSymbol = quotingAsset?.symbol;
    const underlyingAssetSymbol = underlyingAsset?.symbol;

    if (notionalType === NotionalType.Inverse) {
      return quantityToContractsForInverse({
        inputedQuantity,
        selectedProduct,
        selectedCurrency,
      });
    }

    const markPrice = isNan(getMarkPrice(Number(selectedProduct?.tick_size)))
      ? 0
      : Number(getMarkPrice(Number(selectedProduct?.tick_size)));
    if (selectedCurrency === underlyingAssetSymbol) {
      return parseInt(String(divide(inputedQuantity, contractValue)), 10);
    }
    if (selectedCurrency === quotingAssetSymbol) {
      return parseInt(
        String(
          divide(inputedQuantity, Number(decimalMultiplication(contractValue, markPrice)))
        ),
        10
      );
    }
    return inputedQuantity;
  } catch (error) {
    console.error('Error in quantityToContractsForFutures:', error);
    return 0;
  }
};

const contractToAssetForFutures = ({
  orderSize,
  selectedProduct,
  selectedCurrency,
}: {
  orderSize: number;
  selectedProduct: Product;
  selectedCurrency: string;
}) => {
  try {
    const quotingAsset = selectedProduct?.quoting_asset;
    const underlyingAsset = selectedProduct?.underlying_asset;
    const notionalType = selectedProduct?.notional_type;
    const contractValue = Number(selectedProduct?.contract_value ?? '1');

    if (!quotingAsset || !underlyingAsset) {
      throw new Error('Quoting asset and underlying asset are required');
    }

    const quotingAssetSymbol = quotingAsset?.symbol;
    const underlyingAssetSymbol = underlyingAsset?.symbol;

    if (notionalType === NotionalType.Inverse) {
      return contractToAssetForInverse({
        orderSize,
        selectedProduct,
        selectedCurrency,
      });
    }

    const markPrice = isNan(getMarkPrice(Number(selectedProduct?.tick_size)))
      ? 0
      : Number(getMarkPrice(Number(selectedProduct?.tick_size)));
    const formula = orderSize * contractValue;
    if (selectedCurrency === underlyingAssetSymbol) {
      return cropAfterDecimals(formula, underlyingAsset?.minimum_precision ?? 2);
    }
    if (selectedCurrency === quotingAssetSymbol) {
      return cropAfterDecimals(
        Number(decimalMultiplication(formula, markPrice)),
        quotingAsset?.minimum_precision ?? 2
      );
    }
    return 0;
  } catch (error) {
    console.error('Error in contractToAssetForFutures:', error);
    return 0;
  }
};

const marginCalculationForFutures = (
  size: number,
  price: number,
  leverage: number,
  product: Product
) => {
  try {
    const notion = notional(size, price, product);
    if (isNan(notion)) {
      throw new Error('Notional value is NaN');
    }
    if (!notion) {
      throw new Error('Notional value is zero');
    }
    const margin = divide(notion, leverage);
    return margin;
  } catch (error) {
    console.error('Error in marginCalculationForFutures:', error);
    return null;
  }
};

const computeFuturesMaxQuantity = ({
  availableBalance,
  entryPrice,
  leverage,
  commissionRate,
  notionalType,
  side,
  totalPositionSize,
  product,
}: {
  availableBalance: number;
  entryPrice: number;
  leverage: number;
  commissionRate: number;
  notionalType?: NotionalType;
  side: SIDE;
  totalPositionSize: number;
  product: Product;
}) => {
  try {
    if (availableBalance <= 0) {
      throw new Error('Available balance must be greater than zero');
    }
    if (entryPrice <= 0) {
      throw new Error('Entry price must be greater than zero');
    }
    if (leverage <= 0) {
      throw new Error('Leverage must be greater than zero');
    }
    if (commissionRate < 0) {
      throw new Error('Commission rate cannot be negative');
    }
    const leverageFactor = 1 / leverage;
    const totalCommission = 2 * commissionRate;
    const totalCost = leverageFactor + totalCommission;

    switch (notionalType) {
      case NotionalType.Inverse: {
        const maxQuantity = (entryPrice * availableBalance) / totalCost;
        return sanitizeMaxQuantity({
          maxQuantity,
          totalPositionSize,
          side,
          product,
        });
      }
      case NotionalType.Vanilla: {
        const entryCost = entryPrice * totalCost;
        const maxQuantity = availableBalance / entryCost;
        return sanitizeMaxQuantity({
          maxQuantity,
          totalPositionSize,
          side,
          product,
        });
      }
      default:
        throw new Error('Invalid notional type');
    }
  } catch (error) {
    console.error('Error in computeFuturesMaxQuantity:', error);
    return null;
  }
};

export {
  computeFuturesMaxQuantity,
  contractToAssetForFutures,
  contractToAssetForInverse,
  marginCalculationForFutures,
  quantityToContractsForFutures,
  quantityToContractsForInverse,
};
